import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Download, Paperclip, Loader2, CalendarIcon, X } from "lucide-react";
import { adminApi, errMsg } from "@/lib/api";
import { fmtMoney, fmtDate, ageHours, timeAgo, METHOD_TYPE_LABEL, TXN_LABELS, downloadCSV } from "@/lib/format";
import { PageHeader, StatusBadge, EmptyState, SecureFile, CopyButton, useDebounced } from "@/components/common";
import { Row, StatusTimeline } from "@/components/DepositDetailsSheet";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const STATUSES = ["Pending", "Under Review", "Approved", "Rejected", "Failed", "Refunded"];
const PAGE = 25;
const ACTIONS = {
  Pending: ["Under Review", "Approved", "Rejected", "Failed"],
  "Under Review": ["Approved", "Rejected", "Failed", "Pending"],
  Approved: ["Refunded"],
};
const ACTION_LABEL = { "Under Review": "Mark under review", Approved: "Approve & credit", Rejected: "Reject", Failed: "Mark failed", Pending: "Back to pending", Refunded: "Refund & reverse" };
const ACTION_STYLE = { Approved: "bg-emerald-700 hover:bg-emerald-800 text-white", Rejected: "bg-rose-700 hover:bg-rose-800 text-white", Refunded: "bg-violet-700 hover:bg-violet-800 text-white" };

function ActionDialog({ dep, action, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [confirmed, setConfirmed] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  React.useEffect(() => { if (action) { setNote(""); setErr(""); setConfirmed(String(dep?.amount ?? "")); } }, [action, dep]);
  if (!dep || !action) return null;
  const needsNote = ["Rejected", "Failed", "Refunded"].includes(action);
  const snap = dep.bonus_snapshot || {};
  const c = parseFloat(confirmed) || 0;
  const bonus = snap.eligible ? Math.floor(Math.min(c, snap.max_eligible) * snap.rate_pct / 100 * (dep.currency === "INR" ? 100 : 10000)) / (dep.currency === "INR" ? 100 : 10000) : 0;
  const go = async () => {
    if (needsNote && !note.trim()) return setErr("Please enter a reason");
    if (action === "Approved" && !(c > 0)) return setErr("Enter the confirmed amount");
    setBusy(true);
    setErr("");
    try {
      await adminApi.post(`/admin/deposits/${dep.id}/status`, { status: action, note: note.trim() || null, confirmed_amount: action === "Approved" ? c : null });
      toast.success(`Deposit ${dep.id}: ${action}`);
      onDone();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-action-dialog">
        <DialogHeader>
          <DialogTitle>{ACTION_LABEL[action]} · {dep.id}</DialogTitle>
          <DialogDescription>
            {action === "Approved" ? "Approve only after confirming the payment in your bank / UPI / provider dashboard. A receipt alone is not proof." : action === "Refunded" ? "The deposit and its bonus will be debited from the user's wallet as separate ledger entries." : "The user will be notified of this status change."}
          </DialogDescription>
        </DialogHeader>
        {action === "Approved" && (
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Confirmed amount received ({dep.currency})</Label><Input inputMode="decimal" value={confirmed} onChange={(e) => setConfirmed(e.target.value)} className="num" data-testid="admin-confirmed-amount-input" /></div>
            <div className="space-y-1.5 rounded-lg bg-secondary p-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Deposit credit</span><span className="num">{fmtMoney(c, dep.currency)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Bonus credit {snap.eligible ? `(${snap.rate_pct}% up to ${fmtMoney(snap.max_eligible, dep.currency)})` : "(not eligible)"}</span><span className="num text-emerald-700" data-testid="admin-approve-bonus">{fmtMoney(bonus, dep.currency)}</span></div>
              <div className="flex justify-between border-t pt-1.5 font-semibold"><span>Total</span><span className="num">{fmtMoney(c + bonus, dep.currency)}</span></div>
            </div>
          </div>
        )}
        <div className="space-y-1.5"><Label>{needsNote ? "Reason (shown to user)" : "Internal note (optional)"}</Label><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-action-note-input" /></div>
        {err && <p className="text-sm text-rose-700" data-testid="admin-action-error">{err}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="admin-action-cancel">Cancel</Button>
          <Button onClick={go} disabled={busy} className={ACTION_STYLE[action]} data-testid="admin-action-confirm">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{ACTION_LABEL[action]}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailSheet({ id, onClose }) {
  const qc = useQueryClient();
  const [action, setAction] = useState(null);
  const { data: d, isLoading } = useQuery({ queryKey: ["admin-deposit", id], queryFn: () => adminApi.get(`/admin/deposits/${id}`).then((r) => r.data), enabled: !!id });
  const done = () => {
    setAction(null);
    qc.invalidateQueries({ queryKey: ["admin-deposit", id] });
    qc.invalidateQueries({ queryKey: ["admin-deposits"] });
    qc.invalidateQueries({ queryKey: ["admin-stats-badge"] });
  };
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="admin-deposit-sheet">
        <SheetHeader><SheetTitle className="flex items-center gap-2">Deposit request {d && <StatusBadge status={d.status} />}</SheetTitle></SheetHeader>
        {isLoading || !d ? <Skeleton className="mt-6 h-96" /> : (
          <div className="mt-4 space-y-6">
            {ACTIONS[d.status] && (
              <div className="flex flex-wrap gap-2" data-testid="admin-deposit-actions">
                {ACTIONS[d.status].map((a) => (
                  <Button key={a} size="sm" variant={ACTION_STYLE[a] ? "default" : "outline"} className={ACTION_STYLE[a]} onClick={() => setAction(a)} data-testid={`admin-deposit-action-${a.toLowerCase().replace(" ", "-")}`}>{ACTION_LABEL[a]}</Button>
                ))}
              </div>
            )}
            <div className="divide-y">
              <Row label="Request ID"><span className="inline-flex items-center gap-1"><span className="num" data-testid="admin-deposit-id">{d.id}</span><CopyButton value={d.id} label="" variant="ghost" className="h-7 w-7 p-0" testId="admin-copy-deposit-id" /></span></Row>
              <Row label="User">{d.user_name}<span className="block text-xs font-normal text-muted-foreground">{d.user_email} · {d.user_deposit_count} deposits</span></Row>
              <Row label="Method">{d.payment_method_name} <span className="text-muted-foreground">· {METHOD_TYPE_LABEL[d.method_type]}</span></Row>
              <Row label="Requested amount"><span className="num">{fmtMoney(d.amount, d.currency)}</span></Row>
              {d.confirmed_amount != null && <Row label="Confirmed amount"><span className="num">{fmtMoney(d.confirmed_amount, d.currency)}</span></Row>}
              <Row label="Transaction reference"><span className="inline-flex items-center gap-1"><span className="num" data-testid="admin-deposit-reference">{d.transaction_reference}</span><CopyButton value={d.transaction_reference} label="" variant="ghost" className="h-7 w-7 p-0" testId="admin-copy-reference" /></span></Row>
              <Row label="Bonus (quoted)"><span className="num">{fmtMoney(d.bonus_amount_quoted, d.currency)} {d.bonus_snapshot?.eligible ? `· ${d.bonus_snapshot.rate_pct}%` : "· not eligible"}</span></Row>
              {d.bonus_amount_credited != null && <Row label="Bonus credited"><span className="num text-emerald-700">{fmtMoney(d.bonus_amount_credited, d.currency)}</span></Row>}
              <Row label="Submitted">{fmtDate(d.created_at)} <span className="text-xs text-muted-foreground">({timeAgo(d.created_at)})</span></Row>
              {d.verified_by && <Row label="Verified by">{d.verified_by}<span className="block text-xs font-normal text-muted-foreground">{fmtDate(d.verified_at)}</span></Row>}
              {d.status_reason && <Row label="Reason"><span className="text-rose-700">{d.status_reason}</span></Row>}
            </div>
            <div><p className="mb-2 text-sm font-semibold">Receipt</p><SecureFile url={d.receipt_url} client={adminApi} testId="admin-receipt-viewer" /></div>
            <div><p className="mb-3 text-sm font-semibold">Progress</p><StatusTimeline deposit={d} /></div>
            <div>
              <p className="mb-2 text-sm font-semibold">History</p>
              <ul className="space-y-2" data-testid="admin-deposit-history">
                {d.history?.slice().reverse().map((h, i) => (
                  <li key={i} className="rounded-lg border p-3 text-sm">
                    <div className="flex items-center gap-2"><StatusBadge status={h.status} /><span className="ml-auto text-xs text-muted-foreground">{fmtDate(h.at)}</span></div>
                    <p className="mt-1 text-xs text-muted-foreground">by {h.by} ({h.source}){h.note ? ` — ${h.note}` : ""}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="mb-2 text-sm font-semibold">Ledger entries</p>
              {d.ledger?.length === 0 ? <p className="text-sm text-muted-foreground">No wallet entries yet.</p> : (
                <ul className="divide-y rounded-lg border" data-testid="admin-deposit-ledger">
                  {d.ledger.map((t) => (
                    <li key={t.id} className="flex justify-between px-3 py-2 text-sm"><span>{TXN_LABELS[t.transaction_type]}</span><span className={cn("num", t.amount >= 0 ? "text-emerald-700" : "text-rose-700")}>{t.amount >= 0 ? "+" : "−"}{fmtMoney(Math.abs(t.amount), t.currency)}</span></li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        <ActionDialog dep={d} action={action} onClose={() => setAction(null)} onDone={done} />
      </SheetContent>
    </Sheet>
  );
}

export default function AdminDeposits() {
  const [sp] = useSearchParams();
  const [status, setStatus] = useState(sp.get("status") || "all");
  const [search, setSearch] = useState("");
  const [currency, setCurrency] = useState("all");
  const [methodId, setMethodId] = useState("all");
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState(null);
  const s = useDebounced(search, 350);
  const methodsQ = useQuery({ queryKey: ["admin-methods"], queryFn: () => adminApi.get("/admin/payment-methods", { params: { include_archived: true } }).then((r) => r.data) });
  const params = {
    status: status === "all" ? undefined : status, search: s || undefined, currency: currency === "all" ? undefined : currency,
    method_id: methodId === "all" ? undefined : methodId,
    date_from: range.from ? new Date(new Date(range.from).setHours(0, 0, 0, 0)).toISOString() : undefined,
    date_to: range.to ? new Date(new Date(range.to).setHours(23, 59, 59, 999)).toISOString() : range.from ? new Date(new Date(range.from).setHours(23, 59, 59, 999)).toISOString() : undefined,
  };
  const { data, isLoading } = useQuery({
    queryKey: ["admin-deposits", params, page],
    queryFn: () => adminApi.get("/admin/deposits", { params: { ...params, limit: PAGE, skip: page * PAGE } }).then((r) => r.data),
    refetchInterval: 15000,
  });
  const exportCsv = async () => {
    const r = await adminApi.get("/admin/deposits", { params: { ...params, limit: 500 } });
    downloadCSV(`deposits-${Date.now()}.csv`, r.data.items.map((d) => ({
      request_id: d.id, user_email: d.user_email, method: d.payment_method_name, method_type: d.method_type, currency: d.currency, amount: d.amount,
      confirmed_amount: d.confirmed_amount ?? "", bonus_credited: d.bonus_amount_credited ?? "", transaction_reference: d.transaction_reference,
      status: d.status, verified_by: d.verified_by ?? "", verified_at: d.verified_at ?? "", created_at: d.created_at,
    })));
  };
  const tabs = [["all", "All", data?.all], ...STATUSES.map((x) => [x, x, data?.counts?.[x]])];

  return (
    <div>
      <PageHeader title="Deposit requests" subtitle="Verify payments against your bank/UPI records before approving."
        actions={<Button variant="outline" className="gap-2 bg-white" onClick={exportCsv} data-testid="admin-deposits-export-csv"><Download className="h-4 w-4" /> Export CSV</Button>} />
      <div className="mb-3 flex gap-1 overflow-x-auto scrollbar-thin pb-1" data-testid="admin-table-toolbar">
        {tabs.map(([k, l, n]) => (
          <button key={k} onClick={() => { setStatus(k); setPage(0); }} data-testid={`admin-status-tab-${k.toLowerCase().replace(" ", "-")}`}
            className={cn("flex items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors", status === k ? "border-primary bg-primary text-white" : "bg-white hover:bg-secondary")}>
            {l}<span className={cn("num rounded-full px-1.5 text-[11px]", status === k ? "bg-white/20" : "bg-secondary")}>{n ?? 0}</span>
          </button>
        ))}
      </div>
      <div className="mb-4 flex flex-col gap-2 lg:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Search request ID, reference, user email or name" className="h-10 bg-white pl-9" data-testid="admin-deposits-search-input" />
        </div>
        <Select value={currency} onValueChange={(v) => { setCurrency(v); setPage(0); }}>
          <SelectTrigger className="h-10 bg-white lg:w-36" data-testid="admin-deposits-currency-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All currencies</SelectItem><SelectItem value="INR">INR</SelectItem><SelectItem value="USDT">USDT</SelectItem></SelectContent>
        </Select>
        <Select value={methodId} onValueChange={(v) => { setMethodId(v); setPage(0); }}>
          <SelectTrigger className="h-10 bg-white lg:w-52" data-testid="admin-deposits-method-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All methods</SelectItem>{methodsQ.data?.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-10 justify-start gap-2 bg-white lg:w-60" data-testid="admin-deposits-date-filter">
              <CalendarIcon className="h-4 w-4" />
              {range.from ? `${fmtDate(range.from.toISOString(), false)}${range.to ? ` – ${fmtDate(range.to.toISOString(), false)}` : ""}` : "Any date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar mode="range" selected={range} onSelect={(r) => { setRange(r || { from: undefined, to: undefined }); setPage(0); }} numberOfMonths={1} />
          </PopoverContent>
        </Popover>
        {range.from && <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => setRange({ from: undefined, to: undefined })} aria-label="Clear dates" data-testid="admin-deposits-clear-dates"><X className="h-4 w-4" /></Button>}
      </div>

      {isLoading ? <Skeleton className="h-96 rounded-xl" /> : data?.items?.length === 0 ? <EmptyState title="No deposit requests match" testId="admin-deposits-empty">Adjust filters or check back later.</EmptyState> : (
        <Card className="overflow-hidden">
          <div className="max-h-[70vh] overflow-auto scrollbar-thin">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-white">
                <TableRow><TableHead>Request</TableHead><TableHead>User</TableHead><TableHead>Method</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Reference</TableHead><TableHead>Status</TableHead><TableHead>Submitted</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((d) => {
                  const open = ["Pending", "Under Review"].includes(d.status);
                  const age = ageHours(d.created_at);
                  return (
                    <TableRow key={d.id} className="cursor-pointer hover:bg-muted/60" onClick={() => setOpenId(d.id)} data-testid="admin-deposit-row" data-deposit-id={d.id}>
                      <TableCell className="py-2.5"><span className="flex items-center gap-2">{open && <span title="Age" className={cn("h-2 w-2 rounded-full", age > 24 ? "bg-rose-500" : age > 2 ? "bg-amber-500" : "bg-emerald-500")} />}<span className="num text-xs">{d.id}</span></span></TableCell>
                      <TableCell className="py-2.5"><p className="text-sm">{d.user_name}</p><p className="text-xs text-muted-foreground">{d.user_email}</p></TableCell>
                      <TableCell className="py-2.5 text-sm">{d.payment_method_name}<span className="block text-xs text-muted-foreground">{METHOD_TYPE_LABEL[d.method_type]}</span></TableCell>
                      <TableCell className="num py-2.5 text-right font-medium">{fmtMoney(d.amount, d.currency)}</TableCell>
                      <TableCell className="py-2.5"><span className="num inline-flex items-center gap-1 text-xs">{d.transaction_reference}{d.receipt_url && <Paperclip className="h-3 w-3 text-muted-foreground" aria-label="Has receipt" />}</span></TableCell>
                      <TableCell className="py-2.5"><StatusBadge status={d.status} /></TableCell>
                      <TableCell className="whitespace-nowrap py-2.5 text-xs text-muted-foreground">{fmtDate(d.created_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
      {data?.total > PAGE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{page * PAGE + 1}–{Math.min(data.total, (page + 1) * PAGE)} of {data.total}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="admin-deposits-prev">Previous</Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * PAGE >= data.total} onClick={() => setPage(page + 1)} data-testid="admin-deposits-next">Next</Button>
          </div>
        </div>
      )}
      <DetailSheet id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
